// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { EMPLOYMENT_STATUSES, EMPLOYMENT_TYPES, GENDERS, IDENTITY_DOCUMENT_TYPES } from './enums.js';
import { booleanQuerySchema, codeSchema, countryCodeSchema, emailSchema, isoDateSchema, isoDateTimeSchema, jsonObjectSchema, paginationQuerySchema, phoneSchema, uuidSchema, weeklyOffDaysSchema } from './common.js';
/**
 * Free custom fields on an employee (e.g. ramadanEligible, personalEmail): named scalar values, bounded like every other
 * request field (security gate, Prompt 10) — at most 50 fields, names up to 64 characters, text up to 2,000.
 */
export const EMPLOYEE_CUSTOM_FIELDS_MAX = 50;
export const employeeCustomFieldsSchema = z.record(z.string().min(1).max(64), z.union([z.string().max(2000), z.number(), z.boolean(), z.null()]))
    .refine((v) => Object.keys(v).length <= EMPLOYEE_CUSTOM_FIELDS_MAX, { message: `At most ${EMPLOYEE_CUSTOM_FIELDS_MAX} custom fields` });
export const deviceUserIdSchema = z.string().trim().regex(/^[A-Za-z0-9_-]{1,32}$/, 'Device user id: letters, digits, - _ (max 32)');
/** Fields without creation defaults; `createEmployeeSchema` adds the defaults, `updateEmployeeSchema` must not (a PATCH would otherwise reset them). */
const employeeFieldsSchema = z.object({
    employeeNumber: codeSchema,
    firstName: z.string().trim().min(1).max(80),
    middleName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().min(1).max(80),
    displayName: z.string().trim().min(1).max(160).optional(),
    displayNameAr: z.string().trim().max(160).optional(),
    gender: z.enum(GENDERS),
    dateOfBirth: isoDateSchema.optional(),
    nationalityCode: countryCodeSchema.optional(),
    email: emailSchema.optional(),
    phone: phoneSchema.optional(),
    joiningDate: isoDateSchema,
    employmentStatus: z.enum(EMPLOYMENT_STATUSES),
    employmentType: z.enum(EMPLOYMENT_TYPES),
    branchId: uuidSchema,
    /** Where the employee works inside the branch: a place (site, floor, zone…) of that branch (docs/locations.md); `null` clears it. */
    workLocationId: uuidSchema.nullable().optional(),
    departmentId: uuidSchema.optional(),
    designationId: uuidSchema.optional(),
    /** Primary (line) manager; `null` on PATCH clears it. Drives team visibility and manager-chain approvals. */
    managerEmployeeId: uuidSchema.nullable().optional(),
    /** Secondary / dotted-line manager: also sees the employee as a direct report; backup approver when the primary is absent. */
    secondaryManagerEmployeeId: uuidSchema.nullable().optional(),
    deviceUserId: deviceUserIdSchema.optional(), // auto-assigned when omitted
    cardNumber: z.string().trim().max(64).optional(),
    pin: z.string().regex(/^\d{4,8}$/).optional(),
    weeklyOffDays: weeklyOffDaysSchema.optional(),
    customFields: employeeCustomFieldsSchema.optional(),
});
export const createEmployeeSchema = employeeFieldsSchema.extend({
    gender: z.enum(GENDERS).default('unspecified'),
    employmentStatus: z.enum(EMPLOYMENT_STATUSES).default('active'),
    employmentType: z.enum(EMPLOYMENT_TYPES).default('full_time'),
});
/** A field the PATCH may clear: omitted = untouched, `null` = cleared (what the edit form sends for a field the user emptied). */
const clearable = (field) => field.unwrap().nullable().optional();
/**
 * PATCH body: every field optional and NO defaults, so omitted fields are left untouched. The nullable columns also take
 * `null` to clear them; the required ones (names, joining date, branch, device user id, …) do not.
 */
export const updateEmployeeSchema = employeeFieldsSchema.partial().extend({
    /** `null` resets the display name to first + last name, as creation does when it is omitted. */
    displayName: clearable(employeeFieldsSchema.shape.displayName),
    middleName: clearable(employeeFieldsSchema.shape.middleName),
    displayNameAr: clearable(employeeFieldsSchema.shape.displayNameAr),
    dateOfBirth: clearable(employeeFieldsSchema.shape.dateOfBirth),
    nationalityCode: clearable(employeeFieldsSchema.shape.nationalityCode),
    email: clearable(employeeFieldsSchema.shape.email),
    phone: clearable(employeeFieldsSchema.shape.phone),
    departmentId: clearable(employeeFieldsSchema.shape.departmentId),
    designationId: clearable(employeeFieldsSchema.shape.designationId),
    cardNumber: clearable(employeeFieldsSchema.shape.cardNumber),
    /** `null` = no personal weekly off days: inherit from the branch / organisation. */
    weeklyOffDays: clearable(employeeFieldsSchema.shape.weeklyOffDays),
    exitDate: isoDateSchema.nullable().optional(),
    /** When branch/department/designation/manager/type/status change, the effective date of the change (defaults to today). */
    effectiveFrom: isoDateSchema.optional(),
    changeReason: z.string().max(500).optional(),
});
export const employeeListQuerySchema = paginationQuerySchema.extend({
    search: z.string().trim().max(100).optional(),
    branchId: uuidSchema.optional(),
    /** A group / branch location → the employees of its branches; a place → the employees working in it or below (docs/locations.md). */
    locationId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    designationId: uuidSchema.optional(),
    employmentStatus: z.enum(EMPLOYMENT_STATUSES).optional(),
    employmentType: z.enum(EMPLOYMENT_TYPES).optional(),
    managerEmployeeId: uuidSchema.optional(),
    /** Direct reports of an employee: primary OR secondary manager equals the id (what the team predicate calls "team"). */
    teamOf: uuidSchema.optional(),
    /** Only employees not linked to a membership (any status) or to a pending invitation — candidates for a new login. */
    unlinked: booleanQuerySchema.optional(),
    includeDeleted: booleanQuerySchema.default(false),
});
export const employeeDtoSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    employeeNumber: z.string(),
    firstName: z.string(),
    middleName: z.string().nullable(),
    lastName: z.string(),
    displayName: z.string(),
    displayNameAr: z.string().nullable(),
    photoPath: z.string().nullable(),
    photoUrl: z.string().nullable().optional(),
    gender: z.enum(GENDERS),
    dateOfBirth: isoDateSchema.nullable(),
    nationalityCode: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    joiningDate: isoDateSchema,
    exitDate: isoDateSchema.nullable(),
    employmentStatus: z.enum(EMPLOYMENT_STATUSES),
    employmentType: z.enum(EMPLOYMENT_TYPES),
    branchId: uuidSchema,
    branchName: z.string().optional(),
    /** The place the employee works in inside the branch (null = the branch, no finer place). */
    workLocationId: uuidSchema.nullable().optional(),
    /** That place's path below the branch, e.g. "Site A › Floor 2". */
    workLocationName: z.string().nullable().optional(),
    departmentId: uuidSchema.nullable(),
    departmentName: z.string().nullable().optional(),
    designationId: uuidSchema.nullable(),
    designationName: z.string().nullable().optional(),
    managerEmployeeId: uuidSchema.nullable(),
    managerName: z.string().nullable().optional(),
    secondaryManagerEmployeeId: uuidSchema.nullable().optional(),
    secondaryManagerName: z.string().nullable().optional(),
    userId: uuidSchema.nullable(),
    deviceUserId: z.string(),
    cardNumber: z.string().nullable(),
    fingerprintEnrolled: z.boolean(),
    faceEnrolled: z.boolean(),
    weeklyOffDays: z.array(z.number()).nullable(),
    customFields: jsonObjectSchema,
    deviceSyncSummary: z.object({ total: z.number(), inSync: z.number(), pending: z.number(), failed: z.number(), offline: z.number() }).optional(),
    deletedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
/**
 * The two reporting lines of an employee: the line manager (`primary` — team visibility, first approver, effective-dated in the
 * employment history) and the dotted-line manager (`secondary` — also sees the employee, backup approver when the primary is away).
 */
export const MANAGER_LINES = ['primary', 'secondary'];
export const bulkEmployeeActionSchema = z.discriminatedUnion('action', [
    z.object({ action: z.literal('assign_branch'), employeeIds: z.array(uuidSchema).min(1).max(1000), branchId: uuidSchema, effectiveFrom: isoDateSchema.optional() }),
    z.object({ action: z.literal('assign_department'), employeeIds: z.array(uuidSchema).min(1).max(1000), departmentId: uuidSchema.nullable(), effectiveFrom: isoDateSchema.optional() }),
    z.object({ action: z.literal('assign_shift'), employeeIds: z.array(uuidSchema).min(1).max(1000), shiftId: uuidSchema, effectiveFrom: isoDateSchema, effectiveTo: isoDateSchema.nullable().optional() }),
    z.object({ action: z.literal('sync_devices'), employeeIds: z.array(uuidSchema).min(1).max(1000), deviceIds: z.array(uuidSchema).max(500).optional() }),
    z.object({ action: z.literal('set_status'), employeeIds: z.array(uuidSchema).min(1).max(1000), employmentStatus: z.enum(EMPLOYMENT_STATUSES), effectiveFrom: isoDateSchema.optional() }),
    /** Every selected employee reports to `managerEmployeeId` on the given line; `null` removes that manager. */
    z.object({ action: z.literal('assign_manager'), employeeIds: z.array(uuidSchema).min(1).max(1000), managerEmployeeId: uuidSchema.nullable(), line: z.enum(MANAGER_LINES).default('primary'), effectiveFrom: isoDateSchema.optional() }),
    z.object({ action: z.literal('export'), employeeIds: z.array(uuidSchema).max(10000).optional(), format: z.enum(['csv', 'xlsx']).default('xlsx') }),
]);
export const identityDocumentInputSchema = z.object({
    type: z.enum(IDENTITY_DOCUMENT_TYPES),
    number: z.string().trim().min(1).max(64),
    issuingCountry: countryCodeSchema.optional(),
    issuedAt: isoDateSchema.optional(),
    expiresAt: isoDateSchema.optional(),
    notes: z.string().max(500).optional(),
});
/** CSV/XLSX import row (§44). Column headers of the downloadable template. */
export const employeeImportRowSchema = createEmployeeSchema.omit({ branchId: true, departmentId: true, designationId: true, managerEmployeeId: true, secondaryManagerEmployeeId: true }).extend({
    branchCode: codeSchema,
    departmentCode: codeSchema.optional(),
    designationCode: codeSchema.optional(),
    managerEmployeeNumber: codeSchema.optional(),
    /** Oman civil ID: stored as the employee's `civil_id` identity document (sensitive: needs employee.view_sensitive to import). */
    civilId: z.string().trim().min(1).max(64).optional(),
    /** Last working day; with a terminated / resigned status it is the date the employment ends. */
    exitDate: isoDateSchema.optional(),
});
/**
 * A row that updates an EXISTING employee (matched by employee number): every column optional — an empty cell (or a field the
 * HR system did not send) leaves the stored value unchanged; no creation default is applied.
 */
export const employeeImportUpdateRowSchema = employeeImportRowSchema.omit({ gender: true, employmentStatus: true, employmentType: true }).partial().extend({
    employeeNumber: codeSchema,
    gender: z.enum(GENDERS).optional(),
    employmentStatus: z.enum(EMPLOYMENT_STATUSES).optional(),
    employmentType: z.enum(EMPLOYMENT_TYPES).optional(),
});
export const EMPLOYEE_IMPORT_COLUMNS = ['employeeNumber', 'firstName', 'middleName', 'lastName', 'displayName', 'gender', 'dateOfBirth', 'nationalityCode', 'email', 'phone', 'joiningDate', 'employmentStatus', 'employmentType', 'branchCode', 'departmentCode', 'designationCode', 'managerEmployeeNumber', 'deviceUserId', 'cardNumber', 'displayNameAr', 'civilId', 'exitDate'];
//# sourceMappingURL=employees.js.map